import { Page, View, Text, Image } from '@react-pdf/renderer';
import { StyleSheet } from '@react-pdf/renderer';
import {
  LaudoHeader, Footer, Homogeneizacao, DetalhamentoValores, AvaliacaoFinal,
  ResponsavelLaudo, CalculoAvaliatario, TabelaDepreciacaoItem,
  TabelaAreaConstruidaItem, TabelaValorTerrenoItem,
} from '../../types';
import { AVMPageFooter } from './AVM/components';

const s = StyleSheet.create({
  page: { width: 595, height: 842, backgroundColor: '#FFFFFF', flexDirection: 'column', fontFamily: 'Helvetica', paddingBottom: 40 },
  headerBar: { paddingBottom: 8, borderBottom: '1 solid #D6DBE7', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F6F9', gap: 8, paddingRight: 8 },
  accentBlue: { width: 3, height: 28, backgroundColor: '#0090FF' },
  subHeader: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F6F9', gap: 8, paddingRight: 8, marginTop: 12 },
  accentDark: { width: 3, height: 28, backgroundColor: '#101010' },
  title: { color: '#000000', fontSize: 9, fontWeight: 'bold' },
  label: { color: '#000000', fontSize: 7.5, fontWeight: 'normal' },
  value: { color: '#000000', fontSize: 9, fontWeight: 'bold' },
  dataRow: { flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7' },
  dataRowSpaced: { flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 10, borderBottom: '1 solid #D6DBE7' },
  // Table
  tH: { flexDirection: 'row', height: 16, paddingHorizontal: 4, backgroundColor: '#F5F6F9', alignItems: 'center' },
  tR: { flexDirection: 'row', height: 16, paddingHorizontal: 4, alignItems: 'center' },
  tRA: { flexDirection: 'row', height: 16, paddingHorizontal: 4, backgroundColor: '#F5F6F9', alignItems: 'center' },
  cH: { flex: 1, fontSize: 7, fontWeight: 'bold', color: '#000000', textAlign: 'center' },
  cD: { flex: 1, fontSize: 7, color: '#000000', textAlign: 'center' },
});

// Format factor with 2 decimal places (e.g. "1.000" → "1,00", "0.900" → "0,90")
function fmtF(value: string | undefined | null): string {
  if (!value || value === '-') return '-';
  const num = parseFloat(value.replace(',', '.'));
  if (isNaN(num)) return value;
  return num.toFixed(2).replace('.', ',');
}

// Generic table
function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <View style={{ flexDirection: 'column' }}>
      <View style={s.tH}>
        {headers.map((h, i) => <Text key={i} style={s.cH}>{h}</Text>)}
      </View>
      {rows.map((row, ri) => (
        <View key={ri} style={ri % 2 === 0 ? s.tR : s.tRA}>
          {row.map((c, ci) => <Text key={ci} style={s.cD}>{c}</Text>)}
        </View>
      ))}
    </View>
  );
}

function SubH({ title }: { title: string }) {
  return (
    <View style={s.subHeader} wrap={false}>
      <View style={s.accentDark} />
      <Text style={s.title}>{title}</Text>
    </View>
  );
}

interface NormalCalculosProps {
  header: LaudoHeader;
  // Comparativo direto
  tabelaCalculo: CalculoAvaliatario[];
  legendaFatores: string;
  fatoresAtivos?: number;
  mediaValorUnitarioOriginal: string;
  mediaValorHomogeneizado: string;
  // Evolutivo (optional)
  tabelaDepreciacao?: TabelaDepreciacaoItem[];
  tabelaAreaConstruida?: TabelaAreaConstruidaItem[];
  tabelaValorTerreno?: TabelaValorTerrenoItem[];
  // Common
  homogeneizacao: Homogeneizacao;
  detalhamentoValores: DetalhamentoValores;
  avaliacaoFinal: AvaliacaoFinal;
  responsavelLaudo: ResponsavelLaudo;
  comentarios: string;
  footer: Footer;
  logoBase64?: string;
  metodologia?: string;
  tipoImovel?: string;
  metodologiaAplicada?: string;
  grauPrecisao?: string;
  grauFundamentacao?: string;
}

export function NormalCalculosBody({
  tabelaCalculo, legendaFatores, fatoresAtivos = 6,
  mediaValorUnitarioOriginal, mediaValorHomogeneizado,
  tabelaDepreciacao, tabelaAreaConstruida, tabelaValorTerreno,
  homogeneizacao, detalhamentoValores, avaliacaoFinal, responsavelLaudo,
  comentarios, metodologia, tipoImovel,
  metodologiaAplicada, grauPrecisao, grauFundamentacao,
}: Omit<NormalCalculosProps, 'header' | 'footer' | 'logoBase64'>) {
  const fatoresIndices = Array.from({ length: fatoresAtivos }, (_, i) => i + 1);
  const isEvolutivo = tabelaDepreciacao && tabelaDepreciacao.length > 0;
  const isComparativoDireto = metodologia?.toLowerCase().includes('comparativo') && metodologia?.toLowerCase().includes('direto');
  const isTerreno = (tipoImovel || '').toLowerCase().includes('terreno');

  return (
    <View style={{ flexDirection: 'column' }}>
      {metodologiaAplicada && (
        <View style={{ flexDirection: 'column' }} wrap={false}>
          <View style={s.sectionHeader}>
            <View style={s.accentBlue} />
            <Text style={s.title}>AVALIAÇÃO DO IMÓVEL - CÁLCULOS</Text>
          </View>
          <View style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7' }}>
            <View style={{ width: 176, flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
              <Text style={s.label}>Metodologia</Text>
              <Text style={s.value}>{metodologiaAplicada}</Text>
            </View>
            <View style={{ flex: 1, flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
              <Text style={s.label}>Grau de Precisão</Text>
              <Text style={s.value}>{grauPrecisao || '-'}</Text>
            </View>
            <View style={{ flex: 1, flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
              <Text style={s.label}>Grau de Fundação</Text>
              <Text style={s.value}>{grauFundamentacao || '-'}</Text>
            </View>
          </View>
        </View>
      )}

        {/* ── EVOLUTIVO: 3 tabelas ── */}
        {isEvolutivo && (
          <>
            {/* Depreciação */}
            <SubH title="CÁLCULO DO COEFICIENTE DE DEPRECIAÇÃO" />
            <Table
              headers={['ID / Nome', 'Idade Aparente', 'Estado Conservação', 'Coef. Ross Heidecke', 'Depreciação']}
              rows={tabelaDepreciacao!.map(r => [r.identificacao, r.idadeAparente, r.estadoConservacao, fmtF(r.coefHeidecke), fmtF(r.depreciacao)])}
            />

            {/* Área Construída */}
            <SubH title="CÁLCULO DE VALOR DA ÁREA CONSTRUÍDA" />
            <Table
              headers={['ID / Nome', 'Área (m²)', 'CUB (R$/m²)', 'CUB Ajustado (R$/m²)', 'Valor Final']}
              rows={(tabelaAreaConstruida || []).map(r => [r.identificacao, r.areaConstruida, r.cubBase, r.cubAjustado, r.valorFinal])}
            />

            {/* Valor do Terreno */}
            {tabelaValorTerreno && tabelaValorTerreno.length > 0 && (() => {
              // Detectar quais fatores têm valor em alguma linha
              const hasF = (key: keyof TabelaValorTerrenoItem) =>
                tabelaValorTerreno.some(r => {
                  const v = r[key];
                  if (!v || v === '-') return false;
                  const num = parseFloat(String(v).replace(',', '.'));
                  return !isNaN(num) && num !== 0;
                });

              const allFatorColumns: { key: keyof TabelaValorTerrenoItem; label: string }[] = [
                { key: 'fatorOferta', label: 'F. Oferta' },
                { key: 'fatorTopografia', label: 'F. Topog.' },
                { key: 'fatorArea', label: 'F. Área' },
                { key: 'fatorTestada', label: 'F. Testada' },
                { key: 'fatorVegetacao', label: 'F. Veget.' },
                { key: 'fatorExtraI', label: 'F. Extra I' },
                { key: 'fatorExtraII', label: 'F. Extra II' },
                { key: 'fatorExtraIII', label: 'F. Extra III' },
              ];
              const fatorColumns = allFatorColumns.filter(f => hasF(f.key));

              // Calcular média
              const parseMoney = (s: string) => parseFloat(String(s).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')) || 0;
              const fmtMoney = (n: number) => `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              const mediaFinal = tabelaValorTerreno.reduce((acc, r) => acc + parseMoney(r.valorUnitarioFinal), 0) / tabelaValorTerreno.length;

              return (
                <>
                  <SubH title="CÁLCULO DE VALOR DO TERRENO" />
                  <View style={{ flexDirection: 'column' }}>
                    {/* Header */}
                    <View style={s.tH}>
                      <Text style={[s.cH, { width: 45 }]}>Área (m²)</Text>
                      <Text style={[s.cH, { flex: 1.4 }]}>Valor Terreno</Text>
                      <Text style={[s.cH, { flex: 1.2 }]}>Valor Unit.</Text>
                      <Text style={[s.cH, { width: 40 }]}>I. Fiscal</Text>
                      {fatorColumns.map(f => (
                        <Text key={f.key} style={[s.cH, { flex: 1 }]}>{f.label}</Text>
                      ))}
                      <Text style={[s.cH, { width: 36 }]}>FAT</Text>
                      <Text style={[s.cH, { width: 28 }]}>SAN</Text>
                      <Text style={[s.cH, { flex: 1.3 }]}>Valor Unit. Final</Text>
                    </View>
                    {/* Rows */}
                    {tabelaValorTerreno.map((r, ri) => (
                      <View key={ri} style={ri % 2 === 0 ? s.tR : s.tRA}>
                        <Text style={[s.cD, { width: 45 }]}>{r.areaTerreno}</Text>
                        <Text style={[s.cD, { flex: 1.4 }]}>{r.valorTerreno}</Text>
                        <Text style={[s.cD, { flex: 1.2 }]}>{r.valorUnitario}</Text>
                        <Text style={[s.cD, { width: 40 }]}>{fmtF(r.indiceFiscal)}</Text>
                        {fatorColumns.map(f => (
                          <Text key={f.key} style={[s.cD, { flex: 1 }]}>{fmtF(r[f.key] as string)}</Text>
                        ))}
                        <Text style={[s.cD, { width: 36 }]}>{fmtF(r.fat)}</Text>
                        <Text style={[s.cD, { width: 28 }]}>S</Text>
                        <Text style={[s.cD, { flex: 1.3 }]}>{r.valorUnitarioFinal}</Text>
                      </View>
                    ))}
                    {/* Média row - estrutura idêntica à linha de dados para alinhar perfeitamente */}
                    <View style={{ flexDirection: 'row', height: 20, paddingHorizontal: 4, borderTop: '1 solid #D6DBE7', alignItems: 'center' }}>
                      <Text style={[s.cD, { width: 45 }]}></Text>
                      <Text style={[s.cD, { flex: 1.4, fontWeight: 'bold' }]}>{fmtMoney(mediaFinal)}</Text>
                      <Text style={[s.cD, { flex: 1.2 }]}></Text>
                      <Text style={[s.cD, { width: 40 }]}></Text>
                      {fatorColumns.map(f => <Text key={f.key} style={[s.cD, { flex: 1 }]}></Text>)}
                      <Text style={[s.cD, { width: 36 }]}></Text>
                      <Text style={[s.cD, { width: 28 }]}></Text>
                      <Text style={[s.cD, { flex: 1.3, fontWeight: 'bold' }]}>{fmtMoney(mediaFinal)}</Text>
                    </View>
                  </View>
                </>
              );
            })()}
          </>
        )}

        {/* ── COMPARATIVO DIRETO: Tabela de cálculos ── */}
        {!isEvolutivo && (
          <>
            <SubH title="CÁLCULOS AVALIATÓRIOS" />
            <View style={{ flexDirection: 'column' }}>
              <View style={s.tH}>
                <Text style={[s.cH, { width: 20 }]}>ID</Text>
                <Text style={[s.cH, { width: 44 }]}>Área (m²)</Text>
                <Text style={[s.cH, { width: 80 }]}>Valor Unit.(R$/m²)</Text>
                {fatoresIndices.map(i => <Text key={i} style={[s.cH, { width: 28 }]}>F{i}</Text>)}
                <Text style={[s.cH, { width: 28 }]}>FAT</Text>
                <Text style={[s.cH, { width: 28 }]}>SAN</Text>
                <Text style={[s.cH, { flex: 1 }]}>Valor Hom. (R$/m²)</Text>
              </View>
              {tabelaCalculo.map((row, ri) => (
                <View key={ri} style={ri % 2 === 0 ? s.tR : s.tRA}>
                  <Text style={[s.cD, { width: 20 }]}>{row.id}</Text>
                  <Text style={[s.cD, { width: 44 }]}>{row.area}</Text>
                  <Text style={[s.cD, { width: 80 }]}>{row.valorUnit}</Text>
                  {fatoresIndices.map(idx => (
                    <Text key={idx} style={[s.cD, { width: 28 }]}>
                      {fmtF(row[`F${idx}` as keyof CalculoAvaliatario] as string)}
                    </Text>
                  ))}
                  <Text style={[s.cD, { width: 28 }]}>{fmtF(row.FAT)}</Text>
                  <Text style={[s.cD, { width: 28 }]}>{row.SAN}</Text>
                  <Text style={[s.cD, { flex: 1 }]}>{row.valorHomog}</Text>
                </View>
              ))}
              {/* Totals - estrutura idêntica às linhas de dados para alinhar */}
              <View style={s.tRA}>
                <Text style={[s.cD, { width: 20 }]}></Text>
                <Text style={[s.cD, { width: 44 }]}></Text>
                <Text style={[s.cD, { width: 80, fontWeight: 'bold' }]}>{mediaValorUnitarioOriginal}</Text>
                {fatoresIndices.map(i => <Text key={i} style={[s.cD, { width: 28 }]}></Text>)}
                <Text style={[s.cD, { width: 28 }]}></Text>
                <Text style={[s.cD, { width: 28 }]}></Text>
                <Text style={[s.cD, { flex: 1, fontWeight: 'bold' }]}>{mediaValorHomogeneizado}</Text>
              </View>
            </View>
            <View style={{ paddingHorizontal: 16, paddingVertical: 4 }}>
              <Text style={{ fontSize: 7, color: '#000000' }}>{legendaFatores}</Text>
            </View>
          </>
        )}

        {/* ── HOMOGENEIZAÇÃO E SANEAMENTO (header azul) ── */}
        <View style={{ marginTop: 12 }}>
          <View style={s.sectionHeader}>
            <View style={s.accentBlue} />
            <Text style={s.title}>HOMOGENEIZAÇÃO E SANEAMENTO</Text>
          </View>
        </View>
        <View style={s.dataRow}>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
            <Text style={s.label}>Nº Elementos</Text>
            <Text style={s.value}>{homogeneizacao.nElementos}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
            <Text style={s.label}>Valor Unitário Médio</Text>
            <Text style={s.value}>{homogeneizacao.valorUnitarioMedio}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
            <Text style={s.label}>Limite inferior -30%</Text>
            <Text style={s.value}>{homogeneizacao.limiteInferior30}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
            <Text style={s.label}>Limite superior +30%</Text>
            <Text style={s.value}>{homogeneizacao.limiteSuperior30}</Text>
          </View>
        </View>

        {/* ══ DETALHAMENTO + AVALIAÇÃO FINAL sempre juntos em nova página ══ */}
        <View break style={{ flexDirection: 'column', gap: 24 }}>

          {/* ── DETALHAMENTO DOS VALORES (header azul) ── */}
          <View>
            <View style={s.sectionHeader}>
              <View style={s.accentBlue} />
              <Text style={s.title}>DETALHAMENTO DOS VALORES</Text>
            </View>

            {/* Área Averbada (4 colunas flex:1) */}
            {!isTerreno && (
              <View style={s.dataRowSpaced}>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>{isComparativoDireto ? 'Área Averbada (m²)' : 'Área Construída (m²)'}</Text>
                  <Text style={s.value}>{detalhamentoValores.areaConstruida}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Valor Unitário (R$/m²)</Text>
                  <Text style={s.value}>{detalhamentoValores.valorUnitarioConstruida}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Valor Parcial (R$)</Text>
                  <Text style={s.value}>{detalhamentoValores.valorParcialConstruida}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Valor de Avaliação</Text>
                  <Text style={s.value}>{detalhamentoValores.valorTotal}</Text>
                </View>
              </View>
            )}

            {/* Área Não Averbada */}
            {detalhamentoValores.areaNaoAverbada && (
              <View style={s.dataRowSpaced}>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Área não Averbada (m²)</Text>
                  <Text style={s.value}>{detalhamentoValores.areaNaoAverbada}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Valor Unitário (R$/m²)</Text>
                  <Text style={s.value}>{detalhamentoValores.valorUnitarioNaoAverbada || '-'}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Valor Parcial (R$)</Text>
                  <Text style={s.value}>{detalhamentoValores.valorParcialNaoAverbada || '-'}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Valor de Avaliação</Text>
                  <Text style={s.value}>{detalhamentoValores.valorTotal}</Text>
                </View>
              </View>
            )}

            {/* Terreno */}
            {!isComparativoDireto && (
              <View style={s.dataRowSpaced}>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Área do terreno (m²)</Text>
                  <Text style={s.value}>{detalhamentoValores.areaTerreno}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Valor Unitário (R$/m²)</Text>
                  <Text style={s.value}>{detalhamentoValores.valorUnitarioTerreno}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 3 }}>
                  <Text style={s.label}>Valor Parcial (R$)</Text>
                  <Text style={s.value}>{detalhamentoValores.valorParcialTerreno}</Text>
                </View>
                <View style={{ flex: 1 }} />
              </View>
            )}
          </View>

          {/* ── AVALIAÇÃO FINAL (header azul + dark box com 4 valores) ── */}
          <View wrap={false}>
            <View style={s.sectionHeader}>
              <View style={s.accentBlue} />
              <Text style={s.title}>AVALIAÇÃO FINAL</Text>
            </View>
            <View style={{ backgroundColor: '#001F3F', paddingHorizontal: 40, paddingVertical: 20, marginTop: 16, flexDirection: 'column', gap: 16 }}>
              {/* Row 1: Garantia + Leilão */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flexDirection: 'column', gap: 2 }}>
                  <Text style={{ color: '#FFFFFF', fontSize: 10 }}>Valor para efeito de garantia</Text>
                  <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 'bold' }}>{avaliacaoFinal.valorAvaliacaoGarantia}</Text>
                </View>
                <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <Text style={{ color: '#FFFFFF', fontSize: 10, textAlign: 'right' }}>Valor de garantia para efeito de leilão</Text>
                  <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 'bold', textAlign: 'right' }}>{avaliacaoFinal.valorGarantiaLeilao}</Text>
                </View>
              </View>
              {/* Row 2: Valor do imóvel + Leilão do imóvel */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flexDirection: 'column', gap: 2 }}>
                  <Text style={{ color: '#FFFFFF', fontSize: 10 }}>Valor do imóvel</Text>
                  <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 'bold' }}>{avaliacaoFinal.valorAvaliacao}</Text>
                </View>
                <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <Text style={{ color: '#FFFFFF', fontSize: 10, textAlign: 'right' }}>Valor do imóvel para efeito de leilão</Text>
                  <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 'bold', textAlign: 'right' }}>{avaliacaoFinal.valorImovelLeilao ?? avaliacaoFinal.valorGarantiaLeilao}</Text>
                </View>
              </View>
            </View>
          </View>

        </View>

        {/* ── COMENTÁRIOS (header azul) - só renderiza se preenchido ── */}
        {comentarios && comentarios.trim() && (
          <View style={{ marginTop: 24 }}>
            <View style={s.sectionHeader}>
              <View style={s.accentBlue} />
              <Text style={s.title}>COMENTÁRIOS</Text>
            </View>
            <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7' }}>
              <Text style={{ fontSize: 9, color: '#000000', lineHeight: 1.5 }}>
                {comentarios}
              </Text>
            </View>
          </View>
        )}

        {/* ── RESPONSÁVEL PELO LAUDO (header azul) ── */}
        <View wrap={false} style={{ marginTop: 24 }}>
          <View style={s.sectionHeader}>
            <View style={s.accentBlue} />
            <Text style={s.title}>RESPONSÁVEL PELO LAUDO</Text>
          </View>
          <View style={{ paddingHorizontal: 8, paddingVertical: 6, alignItems: 'center' }}>
            <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#000000' }}>{responsavelLaudo.localData}</Text>
          </View>
          <View style={{ alignItems: 'center', paddingTop: 32, gap: 8 }}>
            {responsavelLaudo.imagemAssinaturaCarimbo ? (
              <Image src={responsavelLaudo.imagemAssinaturaCarimbo} style={{ width: 160, height: 50, objectFit: 'contain' }} />
            ) : (
              <View style={{ width: 224, borderBottom: '1 solid #D6DBE7' }} />
            )}
            <View style={{ alignItems: 'center', gap: 2 }}>
              <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#000000', textAlign: 'center' }}>{responsavelLaudo.nomeResponsavel}</Text>
              <Text style={{ fontSize: 9, color: '#000000', textAlign: 'center' }}>{responsavelLaudo.creaResponsavel}</Text>
            </View>
          </View>
        </View>
      </View>
  );
}

export function NormalCalculos(props: NormalCalculosProps) {
  const { header, footer, logoBase64 } = props;
  return (
    <Page size="A4" style={s.page}>
      {/* Fixed header - só logo/título, repete em todas as páginas */}
      <View fixed style={{ paddingHorizontal: 16, paddingTop: 16 }}>
        <View style={s.headerBar}>
          {logoBase64 ? (
            <Image src={logoBase64} style={{ width: 64, height: 24, objectFit: 'contain' }} />
          ) : (
            <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#101010' }}>bari.</Text>
          )}
          <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <Text style={{ textAlign: 'right', color: '#000000', fontSize: 10, fontWeight: 'bold' }}>LAUDO DE AVALIAÇÃO</Text>
            <Text style={{ textAlign: 'right', color: '#000000', fontSize: 9 }}>{header.idLaudo}  ·  {header.formatoLaudo}</Text>
          </View>
        </View>
      </View>

      <View style={{ paddingHorizontal: 16, paddingTop: 16 }}>
        <NormalCalculosBody {...props} />
      </View>

      <AVMPageFooter footer={footer} />
    </Page>
  );
}
